import { useApiClient } from 'src/hooks/useApiClient';
// @ts-ignore
import './StellantisQuotesPage.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toaster from 'src/utils/toaster';
import i18next from 'i18next';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import TableContainer from 'src/components/genericTable/TableContainer';
import { columnsData } from './columnsData';
import { StellantisQuoteRequestDTO } from 'src/api/models/StellantisDTO';
import { useErrorToast } from 'src/hooks/useErrorToast';
import { FloatingToolbar, StellantisUtils } from 'src/components/Stellantis';
import { Storage } from 'aws-amplify';
import { FaRegFilePdf } from "react-icons/fa";
import ToolTip from 'src/components/Tooltip/Tooltip';
import { MdAddCircle, MdInfoOutline } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { SegmentedControl } from 'src/components/SegmentedControl/SegmentedControl';
import { useAuth } from 'src/hooks/useAuth';
import { DealerDTO } from 'src/api';
import { dealersMap } from 'src/utils/users';

type SalesSegment = 'all' | 'mine' | 'others';

const StellantisQuotesPage = () => {
  const { apiClient, handleApi } = useApiClient();
  const [response, setResponse] = useState<StellantisQuoteRequestDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingState, setLoadingState] = useState({ downloadQuotePdf: false, sendMail: false });
  const [salesSegment, setSalesSegment] = useState<SalesSegment>('all');
  const { showError } = useErrorToast();
  const { user, userReferencePartnerList } = useAuth()
  const [loggedUserDealer, setLoggedUserDealer] = useState<DealerDTO | undefined>();
  const userRefPartner = userReferencePartnerList?.[0]?.toUpperCase();

  const getQuotes = async () => {
    setLoading(true);
    return handleApi(apiClient.stellantisControllerService.getQuotes())
      .then((res) => {
        setResponse(StellantisUtils.mapQuoteRequests(res));
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
        toaster.error(err.message);
      });
  }

  useEffect(() => {
    if (!userRefPartner) {
      setLoggedUserDealer(undefined);
      return;
    }

    handleApi(apiClient.dealerController.getRegisterDealers())
      .then((res) => {
        for (let item of res) {
          let ragioneSociale = item.ragione_sociale
            .toUpperCase()
            .replaceAll('_', ' ');

          if (
            ragioneSociale === userRefPartner ||
            (dealersMap[item.ragione_sociale] !== undefined &&
              dealersMap[item.ragione_sociale] === userRefPartner)
          ) {
            setLoggedUserDealer(item);
            return;
          }
        }
      })
      .catch(console.error);
  }, [userRefPartner])

  useEffect(() => { getQuotes() }, []);

  const showDealerSegment = useMemo(() => {
    if (!loggedUserDealer) return false;
    return loggedUserDealer?.referenti_dealer?.includes(user.username);
  }, [loggedUserDealer])

  const downloadQuotePdf = async (quote: StellantisQuoteRequestDTO) => {
    try {
      if (!quote.hmpPdf?.key) return;
      setLoadingState(prev => ({ ...prev, downloadQuotePdf: true }));

      const [_public, rest] = quote.hmpPdf.key.split('public/');
      const key = rest ?? _public;
      const downloadLink: string = await Storage.get(key, {
        bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_STELLANTIS_DOCUMENTS,
      });
      window.open(downloadLink, '_blank');
    } catch (error) {
      showError(error, i18next.t('download_quote_pdf_failed'))
    } finally {
      setLoadingState(prev => ({ ...prev, downloadQuotePdf: false }));
    }
  };

  const renderActions = useCallback(({ row }) => {
    const isQuoted = StellantisUtils.isQuoted(row?.original);
    let items: any[] = []


    if (StellantisUtils.isUnquotable(row?.original)) {
      const anchorId = `unquotable_reason_${row.id}`;
      return (
        <Button
          id={anchorId}
          key={anchorId}
          className="fw-bold p-0"
          variant="link"
          size="sm">
          <ToolTip
            anchorId={anchorId}
            className="xed"
            place="left"
            content={row?.original?.unquotableReason}
          />
          <MdInfoOutline size={24} className="text-danger p-0 m-0" />
        </Button>
      )
    }

    if (!isQuoted) return <></>;

    items = [
      <Button
        onClick={(e) => {
          e.preventDefault();
          downloadQuotePdf(row.original)
        }}
        variant="link"
        className="text-white"
        id={`info-download-quotation-${row.id}`}
        disabled={loadingState.downloadQuotePdf}>
        {loadingState.downloadQuotePdf
          ? <Spinner animation="border" size="sm" />
          : <FaRegFilePdf size={24} />
        }
        <ToolTip
          anchorId={`info-download-quotation-${row.id}`}
          content={i18next.t('download_quotation')}
          className="xed"
          place="left"
        />
      </Button>,
      <Link
        to={`/stellantis-quotes/${row.original.id}/opportunity-wizard`}
        className="btn text-white"
        id={`info-create-opportunity-${row.id}`}
      >
        <MdAddCircle
          style={{ width: '1.2rem', height: '1.2rem' }}
          name="create"
        />
        <ToolTip
          anchorId={`info-create-opportunity-${row.id}`}
          className="xed"
          place="left"
          content={i18next.t('create_opportunity_and_send_quote')}
        />
      </Link>
    ]
    return (<FloatingToolbar items={items} />)
  }, [])

  const columns = useMemo(() => {
    return columnsData.map((column) => {
      if (column.id === 'actions') {
        column.Cell = renderActions;
      }
      return column;
    })
  }, []);

  const data = useMemo(() => {
    const all = response || [];
    if (salesSegment === 'mine') {
      return all.filter(item => item.requestedBy?.toLowerCase() === user?.username?.toLowerCase());
    }
    if (salesSegment === 'others') {
      return all.filter(item => item.requestedBy !== user?.username);
    }
    return all;
  }, [response, salesSegment, user?.username]);

  return (
    <Container fluid>
      <Row>
        <Col md={6} className="display-6">
          {i18next.t('stellantis_quotes')}
        </Col>
      </Row>
      <TableContainer
        hover={false}
        columns={columns}
        data={data}
        loading={loading}
        error={error}
        filterSlot={showDealerSegment &&
          <SegmentedControl
            fullWidth
            data={[
              { label: 'Tutti', value: 'all' },
              { label: 'I miei', value: 'mine' },
              { label: 'Altri Sales', value: 'others' },
            ]}
            onChange={(v) => setSalesSegment(v as SalesSegment)}
          />
        }
      ></TableContainer>
    </Container>
  );
};

export default StellantisQuotesPage;
